// Lightweight SVG/CSS chart primitives for the analytics dashboard. No chart
// library — these follow the dataviz method: validated categorical palette,
// thin marks with rounded data-ends, direct labels (the categorical hues warn
// on light-surface contrast, so labels/tables are the required relief), a
// recessive grid, and a per-mark hover tooltip. Colors are CSS custom
// properties scoped to `.viz-root` so they swap with the app's `.dark` class.
import { useState } from 'react';

// Validated palette (see dataviz references/palette.md). Categorical slots for
// workflows; status palette for run states. Values are injected as CSS vars in
// VizRoot so light/dark swap in one place.
export const CATEGORICAL = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
  'var(--series-7)',
  'var(--series-8)',
] as const;

// Map a run status to a status-palette role. Reserved status colors, never
// reused as a categorical series.
export const STATUS_COLOR: Record<string, string> = {
  completed: 'var(--status-good)',
  error: 'var(--status-critical)',
  lost: 'var(--status-warning)',
  canceled: 'var(--status-muted)',
  cancelled: 'var(--status-muted)',
  queued: 'var(--status-info)',
  running: 'var(--status-info)',
  localizing: 'var(--status-info)',
};

export const statusColor = (status: string): string => STATUS_COLOR[status] ?? 'var(--status-muted)';

// Scopes the validated palette + chart chrome as CSS vars, swapping on `.dark`.
export function VizRoot({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`viz-root ${className ?? ''}`}>{children}</div>;
}

// --- Timeseries: runs over time as vertical bars (one bar per day or hour) ---

interface TimeseriesDatum {
  bucket: string; // YYYY-MM-DD or YYYY-MM-DDTHH:00
  runs: number;
  completed: number;
  errors: number;
}

export function Timeseries({ data, granularity = 'day' }: { data: TimeseriesDatum[]; granularity?: 'hour' | 'day' }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 220;
  const PAD = { top: 16, right: 16, bottom: 28, left: 36 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  if (data.length === 0) return <EmptyChart />;

  const maxRuns = Math.max(1, ...data.map((d) => d.runs));
  const n = data.length;
  const y = (v: number) => PAD.top + innerH - (v / maxRuns) * innerH;

  // Even slots across the width; each bar sits inside its slot with a small gap.
  const slot = innerW / n;
  const barW = Math.max(2, Math.min(36, slot * 0.7));
  const cx = (i: number) => PAD.left + slot * (i + 0.5);

  // ~5 gridlines
  const ticks = 4;
  const gridVals = Array.from({ length: ticks + 1 }, (_, i) => Math.round((maxRuns / ticks) * i));

  // x labels: days get first/middle/last; hours get every k-th bar (~8 labels,
  // e.g. every 3h across a day) so the time of day is readable at a glance.
  const hourly = granularity === 'hour';
  const labelIdx = hourly
    ? data.map((_, i) => i).filter((i) => i % Math.max(1, Math.ceil(n / 8)) === 0)
    : [0, Math.floor((n - 1) / 2), n - 1].filter((v, idx, a) => a.indexOf(v) === idx);
  const axisLabel = (b: string) => (hourly ? fmtHour(b) : fmtDay(b));
  const tooltipLabel = (b: string) => (hourly ? `${fmtDay(b.slice(0, 10))}, ${fmtHour(b)}` : fmtDay(b, true));

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Runs over time">
        {/* gridlines */}
        {gridVals.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--gridline)" strokeWidth={1} />
            <text
              x={PAD.left - 6}
              y={y(v) + 3}
              textAnchor="end"
              fontSize={10}
              fill="var(--muted-ink)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {v}
            </text>
          </g>
        ))}
        {/* bars + full-height hit targets */}
        {data.map((d, i) => {
          const h = innerH - (y(d.runs) - PAD.top);
          return (
            <g key={d.bucket}>
              <rect
                x={cx(i) - barW / 2}
                y={y(d.runs)}
                width={barW}
                height={Math.max(0, h)}
                rx={2}
                fill="var(--series-1)"
                opacity={hover === null || hover === i ? 1 : 0.5}
                style={{ transition: 'opacity 0.15s' }}
              />
              <rect
                x={cx(i) - slot / 2}
                y={PAD.top}
                width={slot}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
        {/* x labels */}
        {labelIdx.map((i) => (
          <text key={i} x={cx(i)} y={H - 8} textAnchor="middle" fontSize={10} fill="var(--muted-ink)">
            {axisLabel(data[i].bucket)}
          </text>
        ))}
      </svg>
      {hover !== null && (
        <Tooltip>
          <div className="font-medium">{tooltipLabel(data[hover].bucket)}</div>
          <Row label="Runs" value={data[hover].runs} dot="var(--series-1)" />
          <Row label="Completed" value={data[hover].completed} dot="var(--status-good)" />
          <Row label="Errors" value={data[hover].errors} dot="var(--status-critical)" />
        </Tooltip>
      )}
    </div>
  );
}

// --- Horizontal bars: runs per workflow, with direct value labels -----------

interface BarDatum {
  label: string;
  value: number;
  color?: string;
  sublabel?: string;
}

export function BarList({ data }: { data: BarDatum[] }) {
  const [hover, setHover] = useState<number | null>(null);
  if (data.length === 0) return <EmptyChart />;
  const max = Math.max(1, ...data.map((d) => d.value));

  return (
    <div className="space-y-2.5">
      {data.map((d, i) => (
        <div key={d.label + i} className="group" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-sm truncate" title={d.label}>
              {d.label}
            </span>
            <span className="text-sm font-medium tabular-nums shrink-0">{d.value.toLocaleString()}</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-300"
              style={{
                width: `${Math.max(2, (d.value / max) * 100)}%`,
                backgroundColor: d.color ?? 'var(--series-1)',
                opacity: hover === null || hover === i ? 1 : 0.55,
              }}
            />
          </div>
          {d.sublabel && <div className="text-xs text-muted-foreground mt-0.5">{d.sublabel}</div>}
        </div>
      ))}
    </div>
  );
}

// --- Donut: status breakdown -------------------------------------------------

interface DonutDatum {
  label: string;
  value: number;
  color: string;
}

export function Donut({ data }: { data: DonutDatum[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <EmptyChart />;

  const R = 60;
  const STROKE = 22;
  const C = 2 * Math.PI * R;
  let offset = 0;
  const segments = data.map((d) => {
    const frac = d.value / total;
    const seg = { ...d, frac, dash: frac * C, offset };
    offset += frac * C;
    return seg;
  });

  return (
    <div className="flex items-center gap-6">
      <div className="relative shrink-0">
        <svg viewBox="0 0 160 160" width={160} height={160} role="img" aria-label="Status breakdown">
          <g transform="translate(80,80) rotate(-90)">
            {segments.map((s, i) => (
              <circle
                key={s.label}
                r={R}
                fill="none"
                stroke={s.color}
                strokeWidth={hover === i ? STROKE + 3 : STROKE}
                strokeDasharray={`${Math.max(0, s.dash - 2)} ${C - Math.max(0, s.dash - 2)}`}
                strokeDashoffset={-s.offset}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                style={{ transition: 'stroke-width 0.15s' }}
              />
            ))}
          </g>
          <text
            x={80}
            y={76}
            textAnchor="middle"
            fontSize={22}
            fontWeight={600}
            fill="var(--primary-ink)"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {total.toLocaleString()}
          </text>
          <text x={80} y={94} textAnchor="middle" fontSize={11} fill="var(--muted-ink)">
            total runs
          </text>
        </svg>
      </div>
      <div className="space-y-1.5 min-w-0">
        {segments.map((s, i) => (
          <div
            key={s.label}
            className="flex items-center gap-2 text-sm"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
            <span className="capitalize truncate">{s.label}</span>
            <span className="text-muted-foreground tabular-nums ml-auto">
              {s.value.toLocaleString()} · {Math.round(s.frac * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Shared bits -------------------------------------------------------------

function Row({ label, value, dot }: { label: string; value: number; dot: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: dot }} />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-medium tabular-nums">{value.toLocaleString()}</span>
    </div>
  );
}

function Tooltip({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none absolute top-2 right-2 rounded-md border bg-popover px-3 py-2 shadow-md space-y-1 min-w-[140px]">
      {children}
    </div>
  );
}

function EmptyChart() {
  return <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">No data in this range</div>;
}

function fmtDay(day: string, withYear = false): string {
  const d = new Date(`${day}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });
}

// "YYYY-MM-DDTHH:00" is already wall-clock time in the viewer's zone (the
// backend buckets with their tz), so read the hour straight off the string.
function fmtHour(bucket: string): string {
  return bucket.slice(11, 16);
}
